import { DateTime } from 'luxon';
import type { DailySummaryType, DailySummaryByPeriod } from '@am3nd-org/pointcraft-types';

import { fReportRefundCents, fReportCurrencyCents } from 'src/utils/report-formatters';
import {
  refundsCents,
  netSalesCents,
  grossSalesCents,
  totalCollectedCents,
  expectedDepositCents,
} from 'src/utils/report-metrics';

type Summary = DailySummaryType | DailySummaryByPeriod;

export type SummaryReceiptAmounts = {
  grossSalesCents: number;
  refundCents: number;
  netSalesCents: number;
  taxesCents: number;
  tipsCents: number;
  totalCollectedCents: number;
  transactionCount: number;
  cashSalesCents: number;
  expectedCashCents: number;
  expectedDepositCents: number;
};

export type SummaryReceiptRow = {
  label: string;
  value: string;
  weight?: number;
};

export type MonthlyPeriodParts = {
  prefix: string;
  month: string;
  suffix: string;
};

export function getSummaryReceiptAmounts(summary: Summary): SummaryReceiptAmounts {
  const cashSalesCents = summary.cash_sales_cents ?? 0;

  return {
    grossSalesCents: grossSalesCents(summary),
    refundCents: refundsCents(summary),
    netSalesCents: netSalesCents(summary),
    taxesCents: summary.taxes_cents ?? 0,
    tipsCents: summary.tip_cents ?? 0,
    totalCollectedCents: totalCollectedCents(summary),
    transactionCount: summary.number_of_sales ?? 0,
    cashSalesCents,
    expectedCashCents: cashSalesCents,
    expectedDepositCents: expectedDepositCents(summary),
  };
}

export function getSummaryReceiptSalesRows(values: SummaryReceiptAmounts): SummaryReceiptRow[] {
  return [
    {
      label: 'Gross Sales',
      value: fReportCurrencyCents(values.grossSalesCents),
    },
    { label: 'Refunds', value: fReportRefundCents(values.refundCents), weight: 700 },
    { label: 'Net Sales', value: fReportCurrencyCents(values.netSalesCents) },
    { label: 'Taxes', value: fReportCurrencyCents(values.taxesCents) },
    { label: 'Tips', value: fReportCurrencyCents(values.tipsCents) },
    {
      label: 'Total Collected',
      value: fReportCurrencyCents(values.totalCollectedCents),
      weight: 600,
    },
  ];
}

function reportDate(value: string, timeZone: string): DateTime | null {
  const parsed = DateTime.fromISO(value, { zone: timeZone });
  return parsed.isValid ? parsed : null;
}

export function dailyPeriodLabel(date: string, timeZone: string): string {
  const selected = reportDate(date, timeZone);
  if (!selected) return date;

  return `${selected.startOf('day').toFormat('MMM d, yyyy h:mm a')} - ${selected
    .endOf('day')
    .toFormat('MMM d, yyyy h:mm a')}`;
}

export function monthlyPeriodParts(
  monthLabel: string,
  dateRange: { start: string; end: string },
  timeZone: string
): MonthlyPeriodParts {
  const start = reportDate(dateRange.start, timeZone);
  const end = reportDate(dateRange.end, timeZone);
  const suffix =
    start && end ? ` · ${start.toFormat('MMM d')} - ${end.toFormat('MMM d, yyyy')}` : '';

  return { prefix: 'Month of ', month: monthLabel, suffix };
}

export function generatedAtLabel(generatedAt: Date, timeZone: string): string {
  return `Report generated at ${DateTime.fromJSDate(generatedAt)
    .setZone(timeZone)
    .toFormat('MMM d, yyyy h:mm:ss a')}`;
}
